import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaBolt,
  FaMicrochip,
  FaSearch,
  FaTimes,
  FaClock,
  FaCheckCircle,
} from "react-icons/fa";
import { CiSettings } from "react-icons/ci";
import { MdOutlineSensors } from "react-icons/md";
import { useQuery } from "@tanstack/react-query";
import getApiServices from "../../services/getApiService";
import { getOrPostSystems } from "../../constants/api-end-pionts";
import { formatDisplayName } from "../../utils/textUtils";
import { getSensorStatus, formatResistance } from "../../utils/sensorStatus";

const glassPanel = {
  background: "rgba(13,21,38,0.78)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.08)",
  boxShadow:
    "0 12px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
};

const statusBadgeStyles: Record<
  string,
  { bg: string; border: string; color: string; dot: string; label: string }
> = {
  Active: {
    bg: "rgba(16,185,129,0.12)",
    border: "rgba(16,185,129,0.25)",
    color: "#34d399",
    dot: "#10b981",
    label: "Active",
  },
  Critical: {
    bg: "rgba(239,68,68,0.12)",
    border: "rgba(239,68,68,0.28)",
    color: "#f87171",
    dot: "#ef4444",
    label: "Critical",
  },
  Inactive: {
    bg: "rgba(100,116,139,0.12)",
    border: "rgba(100,116,139,0.20)",
    color: "#94a3b8",
    dot: "#64748b",
    label: "Inactive",
  },
};

const SystemSensorCard = ({
  sensor,
  criticalThreshold = 3.0,
}: {
  sensor: any;
  criticalThreshold?: number;
}) => {
  const { data: latestData } = useQuery({
    queryKey: ["sensor_latest_data", sensor.name],
    queryFn: () =>
      getApiServices(`/be-service/sensors/latestData?deviceId=${sensor.name}`),
    enabled: !!sensor.name,
    refetchInterval: 30000,
  });

  const rVal = latestData?.R ?? latestData?.r ?? latestData?.resistance ?? null;
  const gVal = latestData?.G ?? latestData?.g ?? latestData?.ground ?? null;
  const lastUpdatedRaw = latestData?.timestamp || sensor.lastUpdatedAt;

  const lastUpdated = lastUpdatedRaw ? new Date(lastUpdatedRaw) : null;
  const now = new Date();
  let minutesAgo: number | null = null;
  if (lastUpdated && !isNaN(lastUpdated.getTime())) {
    minutesAgo = Math.max(
      0,
      Math.round((now.getTime() - lastUpdated.getTime()) / 60000)
    );
  }

  const effectiveThreshold =
    latestData?.criticalResistanceValue ??
    sensor.criticalThreshold ??
    sensor.criticalResistanceValue ??
    criticalThreshold;

  const status = getSensorStatus(rVal, lastUpdatedRaw, gVal, effectiveThreshold);
  const badgeStyle = statusBadgeStyles[status] || statusBadgeStyles["Inactive"];

  return (
    <div
      className="group relative rounded-2xl p-4 sm:p-5 transition-all duration-300 overflow-hidden hover:-translate-y-1 cursor-default border flex flex-col justify-between"
      style={{
        background:
          "linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)",
        borderColor:
          status === "Critical"
            ? "rgba(239,68,68,0.25)"
            : "rgba(255,255,255,0.06)",
        boxShadow:
          status === "Critical"
            ? "0 4px 20px rgba(239,68,68,0.12)"
            : "0 4px 24px rgba(0,0,0,0.25)",
      }}
    >
      {/* Dot-grid subtle pattern */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,1) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />

      {/* Subtle glow on hover */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl"
        style={{ boxShadow: "inset 0 0 40px rgba(59,130,246,0.06)" }}
      />

      <div className="relative z-10 flex flex-col gap-3">
        {/* Top: Name & Status Badge */}
        <div className="flex items-start justify-between gap-2.5">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-white truncate group-hover:text-blue-300 transition-colors">
              {formatDisplayName(sensor.name)}
            </div>
          </div>
          <div
            className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border shrink-0 shadow-sm"
            style={{
              background: badgeStyle.bg,
              borderColor: badgeStyle.border,
              color: badgeStyle.color,
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{
                background: badgeStyle.dot,
                boxShadow: `0 0 6px ${badgeStyle.dot}`,
              }}
            />
            <span>{badgeStyle.label}</span>
          </div>
        </div>

        {/* Description */}
        <p className="text-xs text-slate-400 line-clamp-2 min-h-[32px] leading-relaxed">
          {sensor.description || "Connected earth pit telemetry probe"}
        </p>

        {/* Telemetry Reading Pills */}
        <div className="flex items-center gap-2 flex-wrap pt-1">
          {rVal != null ? (
            <div
              className={`text-[11px] px-2.5 py-0.5 rounded-lg font-mono font-bold flex items-center gap-1 border transition-all ${
                status === "Critical"
                  ? "bg-red-500/15 text-red-300 border-red-500/30 shadow-[0_0_10px_rgba(239,68,68,0.2)]"
                  : "bg-emerald-500/15 text-emerald-300 border-emerald-500/25"
              }`}
              title={`Live resistance: ${formatResistance(rVal)} (Limit: ${effectiveThreshold} Ω)`}
            >
              <FaBolt
                className={`w-2.5 h-2.5 ${
                  status === "Critical" ? "text-red-400 animate-pulse" : "text-emerald-400"
                }`}
              />
              <span>{formatResistance(rVal)}</span>
            </div>
          ) : (
            <span className="text-[11px] text-slate-500 font-mono px-2 py-0.5 rounded bg-white/[0.02]">
              No reading
            </span>
          )}

          <span className="text-[10px] bg-slate-800/80 text-slate-400 px-2 py-0.5 rounded-md font-mono border border-slate-700/60">
            #{sensor.sensorExternalId || "N/A"}
          </span>
        </div>
      </div>

      {/* Card Footer: Last Seen */}
      <div
        className="relative z-10 flex items-center justify-between pt-3 mt-3 text-xs text-slate-400"
        style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
      >
        <div className="flex items-center gap-1 text-[11px] text-slate-400">
          <FaClock className="w-2.5 h-2.5 opacity-60" />
          <span>
            {minutesAgo !== null
              ? minutesAgo === 0
                ? "Just now"
                : `${minutesAgo}m ago`
              : "No recent data"}
          </span>
        </div>

        {effectiveThreshold != null && (
          <span className="text-[10px] text-slate-500 font-mono">
            Limit: {Number(effectiveThreshold).toFixed(1)} Ω
          </span>
        )}
      </div>
    </div>
  );
};

type MobileTab = "systems" | "sensors" | "all";

const SystemsList: React.FC = () => {
  const [selectedSystem, setSelectedSystem] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sensorSearchQuery, setSensorSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Responsive mobile/tablet view tab
  const [activeMobileTab, setActiveMobileTab] = useState<MobileTab>("systems");

  const { data: systemsList, isPending } = useQuery({
    queryKey: ["systems_list", page],
    queryFn: () => getApiServices(getOrPostSystems + `?page=${page}&size=${10}`),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const allSystems = useMemo(() => systemsList?.content || [], [systemsList]);

  const filteredSystems = useMemo(() => {
    if (!searchQuery.trim()) return allSystems;
    const q = searchQuery.toLowerCase().trim();
    return allSystems.filter((item: any) => {
      const name = (item.name || "").toLowerCase();
      const desc = (item.description || "").toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [allSystems, searchQuery]);

  useEffect(() => {
    if (systemsList?.totalPages) {
      setTotalPages(systemsList.totalPages);
    }
  }, [systemsList]);

  // Select first system on load if not selected
  useEffect(() => {
    if (filteredSystems.length > 0 && !selectedSystem) {
      setSelectedSystem(filteredSystems[0]);
    }
  }, [filteredSystems, selectedSystem]);

  // When a system is clicked on mobile/tablet, smoothly switch to the sensors tab
  const handleSystemSelect = useCallback((system: any) => {
    setSelectedSystem(system);
    if (typeof window !== "undefined" && window.innerWidth < 1024) {
      setActiveMobileTab("sensors");
    }
  }, []);

  const systemSensors = useMemo(
    () => selectedSystem?.sensors || [],
    [selectedSystem]
  );

  const filteredSensors = useMemo(() => {
    if (!sensorSearchQuery.trim()) return systemSensors;
    const q = sensorSearchQuery.toLowerCase().trim();
    return systemSensors.filter((s: any) => {
      const name = (s.name || "").toLowerCase();
      const desc = (s.description || "").toLowerCase();
      const extId = String(s.sensorExternalId || "");
      return name.includes(q) || desc.includes(q) || extId.includes(q);
    });
  }, [systemSensors, sensorSearchQuery]);

  return (
    <div
      className="flex flex-col min-h-[calc(100vh-64px)] p-3 sm:p-4 md:p-6 text-white"
      style={{
        background: "linear-gradient(160deg, #070c18 0%, #0a0f1e 60%, #08101f 100%)",
      }}
    >
      {/* ── Page Header ─────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3.5 mb-4 sm:mb-5 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div
            className="p-2.5 rounded-2xl flex-shrink-0 shadow-lg"
            style={{
              background:
                "linear-gradient(135deg, rgba(168,85,247,0.2) 0%, rgba(99,102,241,0.15) 100%)",
              border: "1px solid rgba(168,85,247,0.3)",
              boxShadow: "0 4px 20px rgba(168,85,247,0.2)",
            }}
          >
            <CiSettings className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400 stroke-1" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
              Systems
            </h1>
            <p className="text-slate-400 text-xs sm:text-sm mt-0.5">
              Manage grounding distribution systems and monitor live earth pits
            </p>
          </div>
        </div>

        {/* Stats badges */}
        <div className="flex items-center gap-2 sm:gap-2.5 flex-wrap">
          <span
            className="text-xs px-3 py-1.5 rounded-xl border font-semibold text-slate-300"
            style={{
              background: "rgba(255,255,255,0.03)",
              borderColor: "rgba(255,255,255,0.08)",
            }}
          >
            {systemsList?.totalElements ?? allSystems.length} Total Systems
          </span>
          {selectedSystem && (
            <span
              className="text-xs px-3 py-1.5 rounded-xl border font-semibold text-indigo-300"
              style={{
                background: "rgba(99,102,241,0.1)",
                borderColor: "rgba(99,102,241,0.25)",
              }}
            >
              {systemSensors.length} Earth Pits Linked
            </span>
          )}
        </div>
      </div>

      {/* ── Interactive Breadcrumbs Bar ──────────────────────────── */}
      <div
        className="flex items-center gap-1.5 sm:gap-2 py-2 px-3 sm:px-4 rounded-xl mb-3 sm:mb-4 text-xs overflow-x-auto custom-scrollbar flex-shrink-0"
        style={{
          background: "rgba(255,255,255,0.02)",
          border: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <button
          onClick={() => setActiveMobileTab("systems")}
          className="text-slate-400 hover:text-white transition-colors font-medium flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
        >
          <CiSettings className="w-3.5 h-3.5 text-purple-400 stroke-1" />
          <span>All Systems ({allSystems.length})</span>
        </button>

        {selectedSystem && (
          <>
            <span className="text-slate-600">›</span>
            <button
              onClick={() => setActiveMobileTab("sensors")}
              className="text-purple-300 hover:text-white transition-colors font-semibold truncate max-w-[180px] sm:max-w-xs cursor-pointer whitespace-nowrap"
            >
              {formatDisplayName(selectedSystem.name)}
            </button>
            <span className="text-slate-500 text-[11px] whitespace-nowrap hidden xs:inline">
              ({systemSensors.length} {systemSensors.length === 1 ? "pit" : "pits"})
            </span>
          </>
        )}
      </div>

      {/* ── Mobile & Tablet Segmented Tab Bar (< lg) ─────────────── */}
      <div
        className="lg:hidden flex items-center p-1 rounded-2xl mb-3 sm:mb-4 gap-1 overflow-x-auto custom-scrollbar flex-shrink-0"
        style={{
          background: "rgba(13,21,38,0.75)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <button
          onClick={() => setActiveMobileTab("systems")}
          className={`flex-1 min-w-[110px] py-2 px-3 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer whitespace-nowrap ${
            activeMobileTab === "systems"
              ? "bg-purple-600 text-white shadow-md shadow-purple-600/30"
              : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
          }`}
        >
          <CiSettings className="w-3.5 h-3.5 stroke-1" />
          <span>Systems</span>
          <span className="text-[10px] opacity-75 font-mono">({allSystems.length})</span>
        </button>

        <button
          onClick={() => setActiveMobileTab("sensors")}
          className={`flex-1 min-w-[120px] py-2 px-3 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer whitespace-nowrap ${
            activeMobileTab === "sensors"
              ? "bg-indigo-600 text-white shadow-md shadow-indigo-600/30"
              : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
          }`}
        >
          <FaMicrochip className="w-3 h-3" />
          <span>Earth Pits</span>
          <span className="text-[10px] opacity-75 font-mono">({systemSensors.length})</span>
        </button>

        <button
          onClick={() => setActiveMobileTab("all")}
          className={`py-2 px-3 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer whitespace-nowrap ${
            activeMobileTab === "all"
              ? "bg-slate-700 text-white shadow-sm"
              : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
          }`}
        >
          <span>All Panels</span>
        </button>
      </div>

      {/* ── 2-panel Explorer Layout ──────────────────────────────── */}
      <div className="flex flex-col lg:flex-row gap-4 sm:gap-5 flex-1 min-h-0 lg:h-[calc(100vh-190px)]">
        {/* Left Sidebar - Systems List */}
        <aside
          className={`${
            activeMobileTab === "systems" || activeMobileTab === "all"
              ? "flex"
              : "hidden lg:flex"
          } w-full lg:w-80 xl:w-[340px] flex-shrink-0 flex-col rounded-2xl overflow-hidden min-h-[380px] lg:min-h-0 lg:h-full transition-all duration-300`}
          style={glassPanel}
        >
          {/* Sidebar Header */}
          <div
            className="flex items-center justify-between px-4 sm:px-5 py-3.5 sm:py-4 flex-shrink-0"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
          >
            <div className="flex items-center gap-2.5">
              <div
                className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(168,85,247,0.2) 0%, rgba(99,102,241,0.15) 100%)",
                  border: "1px solid rgba(168,85,247,0.3)",
                  boxShadow: "0 0 16px rgba(168,85,247,0.2)",
                }}
              >
                <CiSettings className="w-5 h-5 text-purple-400 stroke-1" />
              </div>
              <div>
                <h2 className="text-sm sm:text-base font-bold text-white tracking-tight">
                  Systems
                </h2>
                <p className="text-[10px] text-slate-400 font-medium">
                  Select a system to inspect
                </p>
              </div>
            </div>

            <span
              className="text-xs font-semibold px-2.5 py-1 rounded-full border"
              style={{
                background: "rgba(168,85,247,0.10)",
                border: "1px solid rgba(168,85,247,0.25)",
                color: "#c084fc",
              }}
            >
              {systemsList?.totalElements || allSystems.length} systems
            </span>
          </div>

          {/* Search Bar */}
          <div
            className="px-3 sm:px-4 py-2.5 flex-shrink-0"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
          >
            <div className="relative flex items-center">
              <FaSearch className="w-3.5 h-3.5 text-slate-400 absolute left-3 pointer-events-none" />
              <input
                type="text"
                placeholder="Search systems…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-8 py-1.5 text-xs rounded-xl text-white placeholder-slate-500 transition-all outline-none"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "rgba(168,85,247,0.5)";
                  e.target.style.boxShadow = "0 0 0 2px rgba(168,85,247,0.15)";
                  e.target.style.background = "rgba(255,255,255,0.06)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "rgba(255,255,255,0.08)";
                  e.target.style.boxShadow = "none";
                  e.target.style.background = "rgba(255,255,255,0.04)";
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 p-1 text-slate-400 hover:text-white transition-colors cursor-pointer"
                  title="Clear search"
                >
                  <FaTimes className="w-2.5 h-2.5" />
                </button>
              )}
            </div>
          </div>

          {/* Systems List */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-3.5 custom-scrollbar min-h-0">
            {isPending ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-8 h-8 border-3 border-purple-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-xs text-slate-400 font-medium">Loading systems…</span>
              </div>
            ) : filteredSystems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-slate-500 text-center px-4">
                <div
                  className="p-3.5 rounded-2xl mb-3"
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  <CiSettings className="w-8 h-8 opacity-30 text-purple-400 stroke-1" />
                </div>
                <p className="text-sm font-semibold text-slate-400">
                  {searchQuery ? `No systems match "${searchQuery}"` : "No systems found"}
                </p>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="mt-2 text-[11px] text-purple-400 hover:underline cursor-pointer"
                  >
                    Clear search filter
                  </button>
                )}
              </div>
            ) : (
              <ul className="space-y-2.5">
                {filteredSystems.map((system: any, index: number) => {
                  const active = selectedSystem?.id === system.id;
                  const pitCount = system.sensors?.length || 0;

                  return (
                    <li
                      key={system.id || index}
                      onClick={() => handleSystemSelect(system)}
                      className={`cursor-pointer group relative p-3 sm:p-3.5 rounded-2xl transition-all duration-200 ${
                        active
                          ? "border text-white shadow-lg"
                          : "hover:bg-white/[0.04] border border-white/[0.04] text-slate-300"
                      }`}
                      style={{
                        background: active
                          ? "linear-gradient(135deg, rgba(168,85,247,0.18) 0%, rgba(99,102,241,0.10) 100%)"
                          : "rgba(255,255,255,0.02)",
                        borderColor: active
                          ? "rgba(168,85,247,0.35)"
                          : "rgba(255,255,255,0.04)",
                        boxShadow: active
                          ? "0 6px 20px rgba(168,85,247,0.18), inset 0 1px 0 rgba(255,255,255,0.1)"
                          : "none",
                      }}
                      title={system.description || system.name}
                    >
                      {/* Active indicator bar on left */}
                      {active && (
                        <div
                          className="absolute left-0 top-3 bottom-3 w-1 rounded-r-full"
                          style={{
                            background: "linear-gradient(180deg, #c084fc, #818cf8)",
                            boxShadow: "0 0 8px rgba(192,132,252,0.6)",
                          }}
                        />
                      )}

                      <div className="flex items-center justify-between gap-2.5">
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          {/* Dot indicator */}
                          <div className="relative flex-shrink-0">
                            <div
                              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                                active
                                  ? "bg-purple-400"
                                  : "bg-slate-600 group-hover:bg-slate-500"
                              }`}
                            />
                            {active && (
                              <div className="absolute inset-0 rounded-full bg-purple-400 animate-ping opacity-60" />
                            )}
                          </div>

                          {/* System name */}
                          <span
                            className={`text-xs sm:text-sm font-semibold truncate ${
                              active ? "text-white" : "text-slate-300 group-hover:text-white"
                            }`}
                          >
                            {formatDisplayName(system.name)}
                          </span>
                        </div>

                        {/* Earth pit count badge */}
                        <span
                          className={`text-[10px] sm:text-[11px] px-2 py-0.5 rounded-full border font-semibold flex-shrink-0 transition-colors ${
                            active
                              ? "bg-purple-500/20 text-purple-300 border-purple-500/35"
                              : "bg-slate-800/80 text-slate-400 border-slate-700/60"
                          }`}
                        >
                          {pitCount} {pitCount === 1 ? "pit" : "pits"}
                        </span>
                      </div>

                      {/* Sub-row: Selection status */}
                      <div
                        className="flex items-center justify-between mt-2.5 pt-2.5 text-[10px]"
                        style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
                      >
                        <span
                          className={`font-medium truncate max-w-[180px] ${
                            active ? "text-purple-300/80" : "text-slate-500"
                          }`}
                        >
                          {system.description || "Grounding distribution loop"}
                        </span>

                        <div className="flex items-center gap-1">
                          {active ? (
                            <span className="flex items-center gap-1 font-bold text-purple-300 uppercase tracking-wider">
                              <FaCheckCircle className="w-2.5 h-2.5 text-purple-400" />
                              Active
                            </span>
                          ) : (
                            <span className="text-slate-500 group-hover:text-slate-300 font-semibold uppercase tracking-wider">
                              Select ›
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Pagination Footer */}
          <div
            className="flex justify-between items-center px-4 sm:px-5 py-3 flex-shrink-0"
            style={{
              borderTop: "1px solid rgba(255,255,255,0.07)",
              background: "rgba(0,0,0,0.15)",
            }}
          >
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 0))}
              disabled={page === 0}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:text-slate-400 disabled:hover:bg-transparent transition-all cursor-pointer"
            >
              <FaChevronLeft className="w-3 h-3" />
              <span className="hidden sm:inline text-[11px]">Prev</span>
            </button>

            <span className="text-[11px] font-semibold text-slate-400 font-mono">
              Page {page + 1} / {totalPages || 1}
            </span>

            <button
              onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
              disabled={page >= totalPages - 1}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:text-slate-400 disabled:hover:bg-transparent transition-all cursor-pointer"
            >
              <span className="hidden sm:inline text-[11px]">Next</span>
              <FaChevronRight className="w-3 h-3" />
            </button>
          </div>
        </aside>

        {/* Right Content Panel - Connected Sensors Grid */}
        <main
          className={`${
            activeMobileTab === "sensors" || activeMobileTab === "all"
              ? "flex"
              : "hidden lg:flex"
          } flex-1 rounded-2xl overflow-hidden flex-col min-h-[420px] lg:min-h-0 lg:h-full transition-all duration-300`}
          style={glassPanel}
        >
          {selectedSystem ? (
            <div className="h-full flex flex-col overflow-hidden min-h-0">
              {/* Main Panel Header */}
              <div
                className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-3.5 sm:py-4 flex-shrink-0"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(59,130,246,0.15) 100%)",
                      border: "1px solid rgba(16,185,129,0.3)",
                      boxShadow: "0 0 16px rgba(16,185,129,0.2)",
                    }}
                  >
                    <MdOutlineSensors className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-sm sm:text-base font-bold text-white tracking-tight">
                        Connected Earth Pits
                      </h2>
                      <span className="text-xs text-purple-300 font-semibold truncate max-w-[200px] sm:max-w-xs">
                        — {formatDisplayName(selectedSystem.name)}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-400 font-medium">
                      Real-time resistance telemetry & grounding readings
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span
                    className="text-xs font-semibold px-2.5 py-1 rounded-full border"
                    style={{
                      background: "rgba(16,185,129,0.10)",
                      border: "1px solid rgba(16,185,129,0.25)",
                      color: "#34d399",
                    }}
                  >
                    {systemSensors.length} {systemSensors.length === 1 ? "pit" : "pits"}
                  </span>
                </div>
              </div>

              {/* Sub-header banner: System Overview Card */}
              <div
                className="p-4 sm:p-5 m-4 sm:m-5 rounded-2xl relative overflow-hidden flex-shrink-0 transition-all"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(20, 30, 60, 0.75) 0%, rgba(10, 16, 35, 0.85) 100%)",
                  border: "1px solid rgba(168, 85, 247, 0.22)",
                  boxShadow: "0 8px 30px rgba(0, 0, 0, 0.35)",
                }}
              >
                {/* Ambient glow */}
                <div
                  className="absolute -right-10 -top-10 w-40 h-40 rounded-full pointer-events-none opacity-15 blur-3xl"
                  style={{ background: "#a855f7" }}
                />
                <div
                  className="absolute -left-10 -bottom-10 w-40 h-40 rounded-full pointer-events-none opacity-10 blur-3xl"
                  style={{ background: "#3b82f6" }}
                />

                <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base sm:text-lg font-bold text-white truncate">
                        {formatDisplayName(selectedSystem.name)}
                      </h3>
                      <span className="text-[11px] px-2 py-0.5 rounded-md bg-purple-500/15 text-purple-300 border border-purple-500/25 font-mono">
                        System #{selectedSystem.id?.slice(-6) || "Active"}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1 line-clamp-2 max-w-xl leading-relaxed">
                      {selectedSystem.description ||
                        "Sub-distribution earth pit monitoring loop with linked sensors."}
                    </p>
                  </div>

                  {/* Filter box inside main panel */}
                  {systemSensors.length > 3 && (
                    <div className="relative flex items-center min-w-[200px] sm:min-w-[220px]">
                      <FaSearch className="w-3.5 h-3.5 text-slate-400 absolute left-3 pointer-events-none" />
                      <input
                        type="text"
                        placeholder="Filter earth pits…"
                        value={sensorSearchQuery}
                        onChange={(e) => setSensorSearchQuery(e.target.value)}
                        className="w-full pl-8 pr-8 py-1.5 text-xs rounded-xl text-white placeholder-slate-500 transition-all outline-none"
                        style={{
                          background: "rgba(255,255,255,0.05)",
                          border: "1px solid rgba(255,255,255,0.1)",
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = "rgba(16,185,129,0.5)";
                          e.target.style.boxShadow = "0 0 0 2px rgba(16,185,129,0.15)";
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = "rgba(255,255,255,0.1)";
                          e.target.style.boxShadow = "none";
                        }}
                      />
                      {sensorSearchQuery && (
                        <button
                          onClick={() => setSensorSearchQuery("")}
                          className="absolute right-2.5 p-1 text-slate-400 hover:text-white transition-colors cursor-pointer"
                        >
                          <FaTimes className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Sensor Cards Grid */}
              <div className="flex-1 overflow-y-auto px-4 sm:px-5 pb-5 custom-scrollbar min-h-0">
                {systemSensors.length === 0 ? (
                  <div className="py-20 flex flex-col items-center justify-center text-slate-500 text-center px-4 gap-3">
                    <div
                      className="p-4 rounded-2xl"
                      style={{
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.06)",
                      }}
                    >
                      <MdOutlineSensors className="w-10 h-10 text-slate-600 opacity-40" />
                    </div>
                    <p className="text-sm font-semibold text-slate-300">
                      No Earth Pits Configured
                    </p>
                    <p className="text-xs text-slate-500 max-w-sm">
                      There are currently no sensors or earth pits connected to this
                      distribution system.
                    </p>
                  </div>
                ) : filteredSensors.length === 0 ? (
                  <div className="py-12 text-center text-slate-500 text-xs">
                    No earth pits match "{sensorSearchQuery}".
                    <button
                      onClick={() => setSensorSearchQuery("")}
                      className="block mx-auto mt-2 text-emerald-400 hover:underline cursor-pointer"
                    >
                      Clear filter
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3.5 sm:gap-4">
                    {filteredSensors.map((sensor: any) => (
                      <SystemSensorCard key={sensor.id || sensor.name} sensor={sensor} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-slate-500 text-center px-6 gap-3 py-24">
              <div
                className="p-4 rounded-2xl"
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <CiSettings className="w-12 h-12 text-purple-400 opacity-40 stroke-1" />
              </div>
              <p className="text-sm font-semibold text-slate-300">
                No System Selected
              </p>
              <p className="text-xs text-slate-500 max-w-xs">
                Select a system from the left panel to inspect its linked earth pits
                and real-time resistance telemetry.
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default React.memo(SystemsList);
